import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import Chart from 'react-apexcharts';

const palette = [
  '#1a237e',
  '#00897b',
  '#f59e0b',
  '#7c3aed',
  '#0284c7',
  '#dc2626',
  '#65a30d',
  '#475569',
  '#db2777',
  '#0891b2',
  '#9333ea',
  '#ea580c',
];

function truncateAxisLabel(value, maximumCharacters) {
  const label = String(value || '');
  if (label.length <= maximumCharacters) return label;
  return `${label.slice(0, maximumCharacters - 1).trimEnd()}…`;
}

function countTickAmount(maximumCount, compact = false) {
  if (maximumCount <= 1) return 1;
  if (maximumCount <= 4) return maximumCount;
  return compact ? 3 : 4;
}

function prepareCountyRows(rows) {
  const known = (rows || [])
    .filter((row) => row.key !== 'other')
    .sort((left, right) => Number(right.count || 0) - Number(left.count || 0));
  const other = (rows || []).find((row) => row.key === 'other');
  const visible = known.slice(0, 10);
  const remainingCount = known
    .slice(10)
    .reduce((sum, row) => sum + Number(row.count || 0), 0);

  if (remainingCount > 0) {
    visible.push({
      key: 'remaining_counties',
      label: 'Outside top 10',
      count: remainingCount,
    });
  }
  if (other) visible.push(other);
  return visible;
}

function DistributionCard({
  title,
  description,
  rows,
  populationTotal,
  chartVersion,
  type = 'bar',
  headerAction = null,
  scrollable = false,
}) {
  const visibleRows = (rows || []).filter((row) => Number(row.count || 0) > 0);
  const noData = Number(populationTotal || 0) === 0 || visibleRows.length === 0;
  const labels = visibleRows.map((row) => row.label);
  const counts = visibleRows.map((row) => Number(row.count || 0));
  const maximumCount = Math.max(0, ...counts);
  const categoryLabelWidth = title === 'County' ? 130 : 76;
  const compactCategoryLabelWidth = title === 'County' ? 96 : 64;
  const narrowCategoryLabelWidth = title === 'County' ? 82 : 56;
  const labelCharacterLimit = title === 'County' ? 22 : 12;
  const compactLabelCharacterLimit = title === 'County' ? 15 : 10;
  const narrowLabelCharacterLimit = title === 'County' ? 12 : 8;
  const chartHeight = scrollable
    ? Math.max(315, visibleRows.length * 30)
    : 315;

  const commonTooltip = {
    y: {
      formatter: (value) => {
        const percentage = populationTotal > 0
          ? (Number(value || 0) / populationTotal) * 100
          : 0;
        return `${Number(value || 0).toLocaleString('ro-RO')} clients (${percentage.toFixed(1)}%)`;
      },
    },
  };

  const donutOptions = {
    chart: { type: 'donut', fontFamily: 'inherit' },
    labels,
    colors: palette,
    legend: {
      position: 'bottom',
      fontSize: '12px',
      fontWeight: 600,
      itemMargin: { horizontal: 8, vertical: 3 },
    },
    dataLabels: { enabled: false },
    stroke: { colors: ['#ffffff'], width: 2 },
    plotOptions: {
      pie: {
        donut: {
          size: '62%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Clients',
              formatter: () => Number(populationTotal || 0).toLocaleString('ro-RO'),
            },
          },
        },
      },
    },
    tooltip: commonTooltip,
  };

  const barOptions = {
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
    colors: ['#1a237e'],
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        distributed: false,
        barHeight: labels.length > 10 ? '64%' : '56%',
      },
    },
    dataLabels: { enabled: false },
    grid: { borderColor: '#e2e8f0', strokeDashArray: 4 },
    xaxis: {
      categories: labels,
      min: 0,
      tickAmount: countTickAmount(maximumCount),
      labels: {
        formatter: (value) => Math.round(value).toLocaleString('ro-RO'),
        style: { colors: '#64748b', fontSize: '11px' },
      },
    },
    yaxis: {
      labels: {
        maxWidth: categoryLabelWidth,
        formatter: (value) => truncateAxisLabel(value, labelCharacterLimit),
        style: { colors: '#334155', fontSize: '11px', fontWeight: 600 },
      },
    },
    tooltip: {
      ...commonTooltip,
      x: {
        formatter: (value, context) => (
          labels[context?.dataPointIndex] || value
        ),
      },
    },
    responsive: [
      {
        breakpoint: 700,
        options: {
          grid: { padding: { left: 0, right: 4 } },
          xaxis: {
            categories: labels,
            tickAmount: countTickAmount(maximumCount, true),
            labels: {
              formatter: (value) => Math.round(value).toLocaleString('ro-RO'),
              style: { colors: '#64748b', fontSize: '10px' },
            },
          },
          yaxis: {
            labels: {
              maxWidth: compactCategoryLabelWidth,
              formatter: (value) => truncateAxisLabel(
                value,
                compactLabelCharacterLimit,
              ),
              style: { colors: '#334155', fontSize: '10px', fontWeight: 600 },
            },
          },
        },
      },
      {
        breakpoint: 420,
        options: {
          grid: { padding: { left: 0, right: 2 } },
          xaxis: {
            categories: labels,
            tickAmount: countTickAmount(maximumCount, true),
            labels: {
              formatter: (value) => Math.round(value).toLocaleString('ro-RO'),
              style: { colors: '#64748b', fontSize: '10px' },
            },
          },
          yaxis: {
            labels: {
              maxWidth: narrowCategoryLabelWidth,
              formatter: (value) => truncateAxisLabel(
                value,
                narrowLabelCharacterLimit,
              ),
              style: { colors: '#334155', fontSize: '10px', fontWeight: 600 },
            },
          },
        },
      },
    ],
  };

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 390,
        borderRadius: { xs: '14px', sm: '16px' },
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.07)',
      }}
    >
      <CardContent sx={{ height: '100%', boxSizing: 'border-box', p: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography sx={{ color: '#111827', fontWeight: 800, fontSize: '1rem' }}>
            {title}
          </Typography>
          {headerAction}
        </Stack>
        <Typography sx={{ color: '#64748b', fontSize: '0.76rem', mt: 0.2 }}>
          {description}
        </Typography>
        {noData ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 300 }}>
            <Typography sx={{ color: '#64748b', fontSize: '0.85rem' }}>
              No clients in this scope.
            </Typography>
          </Stack>
        ) : (
          <Box
            role="img"
            aria-label={`${title} distribution for ${populationTotal} clients`}
            sx={{
              mt: 1,
              minHeight: 310,
              maxHeight: scrollable ? 315 : 'none',
              overflowY: scrollable ? 'auto' : 'hidden',
              overflowX: 'hidden',
            }}
          >
            <Chart
              key={chartVersion}
              type={type}
              height={chartHeight}
              options={type === 'donut' ? donutOptions : barOptions}
              series={type === 'donut' ? counts : [{ name: 'Clients', data: counts }]}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function ClientsDistributionSection({
  response,
  status,
  scope,
  periodLabel,
  countyLabel = 'All counties',
  onScopeChange,
  onRetry,
}) {
  const [countyView, setCountyView] = React.useState('top');
  const loading = status === 'loading';
  const error = status === 'error';
  const hasData = Boolean(response);
  const populationTotal = Number(response?.population_total || 0);
  // react-apexcharts can retain donut formatter state across partial updates,
  // so a population change must recreate the affected donut charts.
  const donutChartVersion = `${scope}:${response?.generated_at || populationTotal}`;
  const scopeDescription = scope === 'new'
    ? `New registrations · ${periodLabel || 'Selected period'} · ${countyLabel}`
    : `All registered clients · ${countyLabel} · Period filter not applied`;
  const cardDescription = scope === 'new'
    ? `New registrations · ${periodLabel || 'selected period'} · ${countyLabel} · ${populationTotal.toLocaleString('ro-RO')} clients`
    : `All clients · ${countyLabel} · ${populationTotal.toLocaleString('ro-RO')} clients`;
  const countyRowsDescription = countyLabel !== 'All counties'
    ? 'selected county'
    : countyView === 'all' ? 'all counties with clients' : 'top counties';

  return (
    <Box
      component="section"
      aria-labelledby="client-profile-heading"
      sx={{
        p: { xs: 1, sm: 1.5 },
        bgcolor: '#f8fafc',
        border: '1px solid #dbe4ef',
        borderRadius: { xs: '14px', sm: '18px' },
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
      }}
    >
      {loading && hasData && (
        <LinearProgress
          aria-label="Refreshing client profiles"
          sx={{ mb: 1, borderRadius: 999 }}
        />
      )}
      <Box
        sx={{
          p: { xs: 1.25, sm: 1.5 },
          bgcolor: '#fff',
          borderRadius: { xs: '12px', sm: '14px' },
          border: '1px solid #e2e8f0',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={1.25}
        >
          <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: 'rgba(15, 23, 42, 0.08)',
                color: '#0f172a',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <TuneRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box minWidth={0}>
              <Typography id="client-profile-heading" sx={{ fontWeight: 800, color: '#111827', fontSize: '1.05rem' }}>
                Population for all profile charts
              </Typography>
              <Typography sx={{ color: '#64748b', mt: 0.2, fontSize: '0.8rem' }}>
                {scopeDescription}
              </Typography>
            </Box>
          </Stack>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={scope}
            onChange={onScopeChange}
            aria-label="Population applied to all profile charts"
            aria-describedby="client-profile-scope-impact"
            sx={{
              alignSelf: { xs: 'stretch', md: 'center' },
              flexShrink: 0,
              bgcolor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
              '& .MuiToggleButtonGroup-grouped': {
                borderColor: 'rgba(15, 23, 42, 0.08)',
              },
              '& .MuiToggleButton-root': {
                flex: { xs: 1, md: 'initial' },
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.45, sm: 0.5 },
                fontSize: { xs: '0.72rem', sm: '0.78rem' },
                textTransform: 'none',
                fontWeight: 700,
                color: '#334155',
                borderColor: 'rgba(15, 23, 42, 0.08)',
                '&.Mui-selected': {
                  bgcolor: '#0f172a',
                  color: '#fff',
                  '&:hover': { bgcolor: '#111827' },
                },
              },
            }}
          >
            <ToggleButton value="all">All clients</ToggleButton>
            <ToggleButton value="new">New registrations</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Box
          id="client-profile-scope-impact"
          sx={{
            mt: 1.1,
            px: 1,
            py: 0.75,
            bgcolor: '#eff6ff',
            border: '1px solid #dbeafe',
            borderRadius: '10px',
          }}
        >
          <Typography sx={{ color: '#334155', fontSize: { xs: '0.72rem', sm: '0.76rem' }, lineHeight: 1.35 }}>
            <Box component="span" sx={{ color: '#1e3a8a', fontWeight: 800 }}>
              Applies to:
            </Box>{' '}
            Gender, Age, Urban / rural and County.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: { xs: 1, sm: 1.5 } }}>
        {error && !hasData ? (
        <Alert
          severity="error"
          action={(
            <Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={onRetry}>
              Retry
            </Button>
          )}
        >
          Client profile distributions could not be loaded.
        </Alert>
        ) : loading && !hasData ? (
        <Grid container spacing={1.5}>
          {[0, 1, 2, 3].map((key) => (
            <Grid item xs={12} md={6} key={key}>
              <Skeleton variant="rounded" height={390} />
            </Grid>
          ))}
        </Grid>
        ) : (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <DistributionCard
              title="Gender"
              description={cardDescription}
              rows={response?.gender || []}
              populationTotal={populationTotal}
              chartVersion={donutChartVersion}
              type="donut"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DistributionCard
              title="Age"
              description={cardDescription}
              rows={response?.age || []}
              populationTotal={populationTotal}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DistributionCard
              title="Urban / rural"
              description={cardDescription}
              rows={response?.urban_rural || []}
              populationTotal={populationTotal}
              chartVersion={donutChartVersion}
              type="donut"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DistributionCard
              title="County"
              description={`${cardDescription} · ${countyRowsDescription}`}
              rows={
                countyView === 'all'
                  ? response?.county || []
                  : prepareCountyRows(response?.county || [])
              }
              populationTotal={populationTotal}
              scrollable={countyView === 'all'}
              headerAction={countyLabel === 'All counties' ? (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={countyView}
                  onChange={(event, value) => {
                    if (value) setCountyView(value);
                  }}
                  aria-label="County chart rows"
                  sx={{
                    '& .MuiToggleButton-root': {
                      px: 0.9,
                      py: 0.25,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.68rem',
                    },
                  }}
                >
                  <ToggleButton value="top">Top 10</ToggleButton>
                  <ToggleButton value="all">All</ToggleButton>
                </ToggleButtonGroup>
              ) : null}
            />
          </Grid>
        </Grid>
        )}
      </Box>
    </Box>
  );
}

export default ClientsDistributionSection;
